import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../../../Components/Layout";
import PersonIcon from "@mui/icons-material/Person";
import RapInfoProfile from "../RapInfoProfile";
import WageRate from "../WageRate";
import WpsActivities from "../WpsActivities";
import RelatedInstructionDescriptions from "../RelatedInstructionDescription";
import SelectionProcedures from "../SelectionProcedures";
import RAPDocuments from "../RapDocuments";
import DetailPageLayout from "../../../../Components/Layout/DetailPageLayout";
import RapRatio from "../RapRatio";
import MentorPool from "../MentorPool";
import RTIHours from "../RTIHours";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { apprenticeRapPermissionSelector } from "../../../../store/features/permissions/permissionsSlice";
import usePermission from "../../../../../utils/customHooks/usePermission";

export default function RapDetail() {
  const { tab, id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("");
  const isAddMode = useMemo(() => id === "add", [id]);
  const [rapSubTitle, setRapSubTitle] = useState("");
  const showRTIHours = useFeatureFlagEnabled("show-rti-hours-for-apprentice-180-PSPM-695");
  const permissions = usePermission(apprenticeRapPermissionSelector, [
    "Rap Info",
    "Rap Ratio",
    "Wage Rate",
    "WPS Activities",
    "Related Instruction Descriptions",
    "Selection Procedures",
    "RAP Documents",
    "Mentor Pool",
    "RTI Hours"
  ]);

  const {
    "Rap Info": rapInfoPermission,
    "Rap Ratio": rapRatioPermission,
    "Wage Rate": wageRatePermission,
    "WPS Activities": wpsActivitiesPermission,
    "Related Instruction Descriptions": relatedInstructionDescriptionsPermission,
    "Selection Procedures": selectionProceduresPermission,
    "RAP Documents": rapDocumentsPermission,
    "Mentor Pool": mentorPoolPermission,
    "RTI Hours": rtiHoursPermission
  } = permissions;

  const baseTabs = [
    { to: "/rapinfo", title: "RAP Info", key: "rapinfo", icon: PersonIcon, canView: rapInfoPermission.canView },
    { to: "/rap-ratio", title: "RAP Ratio", key: "rap-ratio", icon: PersonIcon, canView: rapRatioPermission.canView },
    { to: "/wagerate", title: "Wage Rate", key: "wagerate", icon: PersonIcon, canView: wageRatePermission.canView },
    {
      to: "/wps-activities",
      title: "WPS Activities",
      key: "wps-activities",
      icon: PersonIcon,
      canView: wpsActivitiesPermission.canView
    },
    {
      to: "/related-instruction-descriptions",
      title: "Related Instruction Descriptions",
      key: "related-instruction-descriptions",
      icon: PersonIcon,
      canView: relatedInstructionDescriptionsPermission.canView
    },
    {
      to: "/selection-procedures",
      title: "Selection Procedures",
      key: "selection-procedures",
      icon: PersonIcon,
      canView: selectionProceduresPermission.canView
    },
    {
      to: "/rap-documents",
      title: "RAP Documents",
      key: "rap-documents",
      icon: PersonIcon,
      canView: rapDocumentsPermission.canView
    },
    {
      to: "/mentor-pool",
      title: "Mentor Pool",
      key: "mentor-pool",
      icon: PersonIcon,
      canView: mentorPoolPermission.canView
    },
    {
      to: "/rti-hours",
      title: "RTI Hours",
      key: "rti-hours",
      icon: PersonIcon,
      canView: rtiHoursPermission.canView && showRTIHours
    }
  ];

  const tabs = isAddMode ? [baseTabs[0]] : baseTabs;

  const backAction = () => {
    navigate("/apprentice-180/rap");
  };

  const handleTabClick = tab => {
    navigate(`/apprentice-180/rap/info/${id}${tab.to}`);
  };

  const getRapInfoProfileData = async id => {
    setRapSubTitle("");
  };

  useEffect(() => {
    if (!isAddMode) getRapInfoProfileData(id);
  }, [id]);

  useEffect(() => {
    const selectedTab = tabs.find(t => t.key === tab?.toLowerCase())?.title;
    setActiveTab(selectedTab || "RAP Info");
  }, [tab]);

  return (
    <Layout>
      {activeTab && (
        <DetailPageLayout
          backAction={backAction}
          tabs={tabs.filter(tab => tab.canView)}
          title={isAddMode ? "ADD RAP INFO" : "Registered Apprenticeship Program"}
          subTitle={isAddMode ? "" : rapSubTitle}
          backLabel="Go back to Rap Info"
          activeTab={activeTab}
          handleTabClick={handleTabClick}
        >
          {activeTab === "RAP Info" && rapInfoPermission.canView && <RapInfoProfile />}
          {activeTab === "RAP Ratio" && rapRatioPermission.canView && <RapRatio />}
          {activeTab === "Wage Rate" && wageRatePermission.canView && <WageRate />}
          {activeTab === "WPS Activities" && wpsActivitiesPermission.canView && <WpsActivities />}
          {activeTab === "Related Instruction Descriptions" &&
            relatedInstructionDescriptionsPermission.canView &&
            <RelatedInstructionDescriptions />}
          {activeTab === "Selection Procedures" && selectionProceduresPermission.canView && <SelectionProcedures />}
          {activeTab === "RAP Documents" && rapDocumentsPermission.canView && <RAPDocuments />}
          {activeTab === "Mentor Pool" && mentorPoolPermission.canView && <MentorPool />}
          {activeTab === "RTI Hours" && rtiHoursPermission.canView && showRTIHours && <RTIHours />}
        </DetailPageLayout>
      )}
    </Layout>
  );
}
